/**
 * Density policy — normative, from docs/ARCHITECTURE.md §4 and
 * docs/CAPACITY-EX44.md §3.2. Pure functions only; no React, no side effects.
 *
 * THE 2x DPR TRAP: adaptive stream sizes the forwarded layer from the tile's
 * *device pixels*. On a retina display a 360 CSS-px tile asks for 720p, and
 * nine of those is 15 Mbps to a single viewer — it collapses the box from ~274
 * concurrent participants to ~46 (study §3.1). So we take the MINIMUM of the
 * role cap and the pixel demand, and the role cap can never be exceeded.
 */
import { LAYER_SPECS, QUALITY_PROFILES, type MediaLayer, type QualityProfile } from '@cyphex/types';

/** Cheapest first. Index order is the comparison order. */
export const LAYER_ORDER: readonly MediaLayer[] = [
  'l0_180p',
  'l1_360p',
  'l2_540p',
  'l3_720p',
  'l4_1080p',
];

const rank = (layer: MediaLayer): number => LAYER_ORDER.indexOf(layer);

export const minLayer = (a: MediaLayer, b: MediaLayer): MediaLayer => (rank(a) <= rank(b) ? a : b);

/** Roles a tile can take. Each has its own hard ceiling. */
export type TileRole = 'spotlight' | 'speaker' | 'grid' | 'secondary' | 'thumbnail' | 'screen';

/**
 * What adaptive stream *would* ask for, given the rendered tile. DPR is capped
 * at 2 because beyond that the extra pixels are invisible at tile size.
 */
export function layerForPixels(cssWidth: number, devicePixelRatio: number): MediaLayer {
  const demand = Math.max(1, cssWidth) * Math.min(Math.max(devicePixelRatio, 1), 2);
  for (const layer of LAYER_ORDER) {
    if (LAYER_SPECS[layer].width >= demand) return layer;
  }
  return 'l4_1080p';
}

/** The ceiling a tile role may never exceed, under the assigned profile. */
export function ceilingForRole(role: TileRole, profile: QualityProfile): MediaLayer {
  const spec = QUALITY_PROFILES[profile];
  switch (role) {
    case 'screen':
      // Screen share is the one place 1080p is allowed (ARCH §4).
      return profile === 'standard' ? 'l4_1080p' : 'l2_540p';
    case 'spotlight':
      return spec.maxSpotlightLayer;
    case 'speaker':
    case 'grid':
      return spec.maxGridLayer;
    case 'secondary':
    case 'thumbnail':
      return spec.maxSecondaryLayer;
  }
}

/**
 * The layer this tile is allowed to receive. Clamped by role first, then by
 * pixels — never the other way round.
 */
export function clampLayer(
  role: TileRole,
  profile: QualityProfile,
  cssWidth: number,
  devicePixelRatio: number,
): MediaLayer {
  const ceiling = ceilingForRole(role, profile);
  if (role === 'screen') return ceiling;
  return minLayer(ceiling, layerForPixels(cssWidth, devicePixelRatio));
}

/** Wire cost of one forwarded layer, bps (encoder target x 1.10 overhead). */
export const wireCostBps = (layer: MediaLayer): number =>
  Math.round(LAYER_SPECS[layer].bitrate * 1.1);
